import React, { useEffect, useState, Fragment } from 'react';
import { doc, getDoc, updateDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { formatCurrency } from '../Help/helper';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import MoonLoader from 'react-spinners/MoonLoader';

const RequisitionDetails = ({ procurementId, onClose }) => {
  const navigate = useNavigate();
  const [procurement, setProcurement] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPO, setHasPO] = useState(false);
  const [checkingPO, setCheckingPO] = useState(true);

  useEffect(() => {
    const fetchProcurement = async () => {
      if (!procurementId) {
        logger.error('No procurement ID provided');
        return;
      }

      try {
        const docRef = doc(db, 'procurements', procurementId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProcurement({ id: docSnap.id, ...docSnap.data() });
        } else {
          logger.warn('No such document!');
        }

        // Check if PO already exists
        const poQuery = query(
          collection(db, 'purchaseOrders'),
          where('procurementId', '==', procurementId)
        );
        const poSnapshot = await getDocs(poQuery);
        setHasPO(!poSnapshot.empty);
        setCheckingPO(false);
      } catch (error) {
        logger.error('Error fetching document:', error);
        toast.error('Error fetching procurement details');
        setCheckingPO(false);
      }
    };

    fetchProcurement();
  }, [procurementId]);

  const handleGeneratePO = () => {
    navigate(`/procurement/generate-po/${procurementId}`);
  };

  if (!procurement) {
    return (
      <Transition show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gradient-to-br from-gray-900/80 via-gray-900/70 to-gray-900/80 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel className="bg-white rounded-2xl p-8">
                <div className="text-center">
                  <MoonLoader size={60} color="#8B5CF6" />
                  <p className="mt-4 text-sm font-medium text-gray-600">Loading procurement details...</p>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  const handleApprovalChange = async (newStatus) => {
    const procurementRef = doc(db, 'procurements', procurementId);
    const newFields = {
      status: newStatus,
      approvalComment: approvalComment,
      statusUpdateDate: new Date(),
    };

    try {
      setLoading(true);
      await updateDoc(procurementRef, newFields);

      if (newStatus === 'Approved') {
        // Optionally add the procurement to the payables collection if approved
        await addDoc(collection(db, 'payables'), {
          ...procurement,
          ...newFields,
        });
      }

      toast.success(`Procurement ${newStatus.toLowerCase()} successfully.`);
      setProcurement((prev) => ({ ...prev, ...newFields }));
      setLoading(false);
      onClose();
    } catch (error) {
      logger.error('Error updating document: ', error);
      toast.error('Failed to update the procurement.');
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Transition show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gradient-to-br from-gray-900/80 via-gray-900/70 to-gray-900/80 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-4xl transform overflow-hidden text-left align-middle shadow-2xl transition-all bg-white rounded-2xl">
                  {/* Header with gradient background */}
                  <div className="relative bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 px-6 py-6">
                    <button
                      onClick={onClose}
                      className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-white flex items-center gap-3">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                        <DocumentTextIcon className="h-7 w-7 text-white" />
                      </div>
                      Procurement Details
                    </Dialog.Title>
                    <p className="mt-2 text-indigo-100 text-sm font-medium">
                      {procurement.procurementNumber}
                    </p>
                  </div>

                  <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
                    {/* Items Table */}
                    <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-500 to-indigo-600">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-black text-white uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-black text-white uppercase tracking-wider">
                              Quantity
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-black text-white uppercase tracking-wider">
                              Price
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-black text-white uppercase tracking-wider">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {procurement.cartItems.map((item, index) => (
                            <tr key={index} className="hover:bg-purple-50 transition-colors duration-150">
                              <td className="px-3 py-2 text-xs text-gray-900 font-medium">
                                {item.description}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-700">
                                {item.quantity}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-700">
                                {formatCurrency(item.price)}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-900 font-semibold">
                                {formatCurrency(item.price * item.quantity)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Generate PO Section */}
                    {procurement.status === 'Approved' && !checkingPO && (
                      <div className="mt-6 bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-500 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <DocumentArrowUpIcon className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-bold text-blue-900 mb-2">
                              Purchase Order Generation
                            </h4>
                            {hasPO ? (
                              <div className="flex items-center gap-2">
                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                <p className="text-sm text-blue-700 font-medium">
                                  Purchase order already generated for this procurement
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-blue-700">
                                  This procurement has been approved. Generate a formal purchase order to proceed with the order.
                                </p>
                                <button
                                  type="button"
                                  onClick={handleGeneratePO}
                                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-700 shadow-md hover:shadow-lg transform transition-all duration-200 hover:scale-105"
                                >
                                  <DocumentArrowUpIcon className="h-5 w-5" />
                                  Generate Purchase Order
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Approval Section */}
                    <div className="mt-6 space-y-4">
                      <div>
                        <label htmlFor="approvalComment" className="block text-sm font-semibold text-gray-700 mb-2">
                          Approval Comment
                        </label>
                        <textarea
                          id="approvalComment"
                          rows="3"
                          value={approvalComment}
                          onChange={(e) => setApprovalComment(e.target.value)}
                          placeholder="Add a comment for approval or rejection..."
                          className="block w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 bg-gray-50 focus:bg-white"
                        />
                      </div>

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => handleApprovalChange('Approved')}
                          disabled={loading}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white text-sm font-semibold rounded-xl hover:from-emerald-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-[1.02]"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApprovalChange('Rejected')}
                          disabled={loading}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-rose-600 to-red-600 text-white text-sm font-semibold rounded-xl hover:from-rose-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-[1.02]"
                        >
                          <XCircleIcon className="h-5 w-5" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full px-4 py-2.5 bg-gray-600 text-white text-sm font-semibold rounded-xl hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform transition-all duration-200 hover:scale-[1.02]"
                    >
                      Close
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default RequisitionDetails;
